<template>
  <div class="timesheet-list-tab">
    <timesheet-list-toolbar
      :date-from.sync="dateFrom"
      :date-to.sync="dateTo"
      :selected-project-id.sync="selectedProjectId"
      :search-query.sync="searchQuery"
      :row-activity-filter.sync="rowActivityFilter"
      :week-label="weekLabel"
      :project-options="projectOptions"
      :row-activity-filter-options="rowActivityFilterOptions"
      :active-filter-count="activeFilterCount"
      :can-download-timesheet="canDownloadTimesheet"
      :export-disabled="events.length === 0"
      @refresh="fetchEvents"
      @export="$bvModal.show('export-report-modal')"
      @date-from-change="onDateFromChange"
      @date-to-change="fetchEvents"
      @project-change="onProjectChange"
      @apply-current-week="resetToCurrentWeek"
      @shift-week="shiftWeek"
    />

    <div class="list-content">
      <b-overlay :show="loading" rounded="sm">
        <div
          v-if="
            !loading &&
            (selectedProjectId
              ? groupedByProject.tradies.length === 0 &&
                groupedByProject.inactiveTradies.length === 0
              : projectGroups.length === 0)
          "
          class="empty-state"
        >
          <b-icon-clipboard class="empty-icon" />
          <p>
            {{
              searchQuery
                ? 'No tradies match your search.'
                : selectedProjectId
                ? 'No tradies in this project yet.'
                : 'No timesheet events for this period.'
            }}
          </p>
          <b-button
            v-if="!searchQuery && canCreateTimesheet"
            variant="primary"
            size="sm"
            class="mt-2"
            @click="openInviteModal"
          >
            + Invite Tradie
          </b-button>
        </div>

        <div v-else-if="!loading" class="tradie-list">
          <template v-if="!selectedProjectId">
            <timesheet-project-group
              v-for="group in projectGroups"
              :key="group.projectId"
              :group="group"
              :collapsed="!!collapsedProjects[group.projectId]"
              :inactive-collapsed="isInactiveCollapsed(group.projectId)"
              :show-inactive-rows="showInactiveRows"
              :visible-tradies-fn="visibleTradies"
              :is-expanded-fn="isExpanded"
              :selected-day-fn="getSelectedDay"
              :week-days="weekDaysForProject(group.projectId)"
              :can-create-timesheet="canCreateTimesheet"
              :can-edit-timesheet="canEditTimesheet"
              :can-delete-timesheet="canDeleteTimesheet"
              :is-supervisor-tradie="isSupervisorTradie"
              :editing-event-id="editingEventId"
              :edit-form="editForm"
              :max-future-date="maxFutureDate"
              :approving-all="approvingAll"
              @toggle-project="toggleProject(group.projectId)"
              @toggle-inactive="toggleInactiveSection(group.projectId)"
              @row-toggle="toggleExpand"
              @approve-all="approveAllForRow"
              @approve-all-group="approveAllForGroup"
              @clock-out="openClockOutModal"
              @bulk-hours="openBulkHoursModal($event.group)"
              @day-click="handleDayClickPayload"
              @view-event="$refs.entryDetailModal.open($event)"
              @approve="approveEvent"
              @reject="openRejectModal"
              @edit="startEditEvent"
              @save-edit="saveEditEvent"
              @cancel-edit="cancelEditEvent"
              @delete="confirmDelete"
              @edit-leave="openEditLeaveModal"
              @delete-leave="confirmDeleteLeave"
              @lightbox="openLightbox"
            />
          </template>

          <timesheet-project-group
            v-else
            :group="singleProjectGroup"
            :collapsible="false"
            :inactive-collapsed="inactiveSectionCollapsed"
            :show-inactive-rows="showInactiveRows"
            :show-no-entries="true"
            :visible-tradies-fn="visibleTradies"
            :is-expanded-fn="isExpanded"
            :selected-day-fn="getSelectedDay"
            :week-days="weekDays"
            :can-create-timesheet="canCreateTimesheet"
            :can-edit-timesheet="canEditTimesheet"
            :can-delete-timesheet="canDeleteTimesheet"
            :is-supervisor-tradie="isSupervisorTradie"
            :editing-event-id="editingEventId"
            :edit-form="editForm"
            :max-future-date="maxFutureDate"
            :approving-all="approvingAll"
            @toggle-inactive="
              inactiveSectionCollapsed = !inactiveSectionCollapsed
            "
            @row-toggle="toggleExpand"
            @approve-all="approveAllForRow"
            @approve-all-group="approveAllForGroup"
            @clock-out="openClockOutModal"
            @bulk-hours="openBulkHoursModal($event.group)"
            @day-click="handleDayClickPayload"
            @view-event="$refs.entryDetailModal.open($event)"
            @approve="approveEvent"
            @reject="openRejectModal"
            @edit="startEditEvent"
            @save-edit="saveEditEvent"
            @cancel-edit="cancelEditEvent"
            @delete="confirmDelete"
            @edit-leave="openEditLeaveModal"
            @delete-leave="confirmDeleteLeave"
            @lightbox="openLightbox"
          />

          <div v-if="canCreateTimesheet" class="bottom-actions">
            <b-button
              size="sm"
              variant="secondary"
              @click="openAddEntryModal(null)"
            >
              + Add Entry
            </b-button>
            <b-button size="sm" variant="primary" @click="openInviteModal">
              + Invite Tradie
            </b-button>
          </div>
        </div>
      </b-overlay>
    </div>

    <timesheet-add-entry-modal
      :form="addEntryForm"
      :tradie-options="tradieOptions"
      :project-options="modalProjectOptions"
      :today="today"
      :max-future-date="maxFutureDate"
      :submitting="addEntrySubmitting"
      @ok="submitAddEntry"
      @hidden="resetAddEntryForm"
    />

    <timesheet-clock-out-modal
      :form="clockOutForm"
      :today="today"
      @ok="submitClockOut"
      @hidden="resetClockOutForm"
    />

    <timesheet-reject-modal
      :form="rejectForm"
      @ok="submitReject"
      @hidden="resetRejectForm"
    />

    <timesheet-edit-leave-modal
      :form="editLeaveForm"
      :today="today"
      @ok="submitEditLeave"
      @delete="confirmDeleteLeave(editLeaveForm)"
      @hidden="resetEditLeaveForm"
    />

    <timesheet-bulk-hours-modal
      :form="bulkHoursForm"
      :workers="bulkHoursWorkers"
      :today="today"
      :max-future-date="maxFutureDate"
      :submitting="bulkHoursSubmitting"
      @ok="submitBulkHours"
      @hidden="resetBulkHoursForm"
    />

    <timesheet-invite-modal
      :form="inviteForm"
      @ok="submitInvite"
      @hidden="resetInviteForm"
    />

    <timesheet-export-modal
      :export-options="exportOptions"
      :tradie-search.sync="exportTradieSearch"
      :project-options="exportProjectOptions"
      :project-label="exportProjectLabel"
      :filtered-tradie-options="filteredExportTradieOptions"
      :tradie-columns="filteredExportTradieColumns"
      @show="prefillExportDates"
      @ok="generateReport"
    />

    <timesheet-photo-lightbox
      :photos="lightboxPhotos"
      :index="lightboxIndex"
      @hidden="resetLightbox"
      @prev="lightboxPrev"
      @next="lightboxNext"
    />

    <timesheet-entry-detail-modal
      ref="entryDetailModal"
      :can-approve="canEditTimesheet"
      @approve="detailApprove"
      @reject="detailReject"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapGetters } from 'vuex'
import Api from '@/api'
import {
  bizNow,
  bizFromIso,
  combineBusinessDateTime,
  splitBusinessIso,
  getApprovableEvents,
  splitIntoColumns,
  emptyTradieRow,
  applyEventToTradieRow,
  buildTimesheetExportRows,
  leaveTypeLabel,
} from '@/utils/timesheetHelpers'
import { startDayOf, addDays, daySpan } from '@/utils/weekHelpers'
import { downloadRowsAsPdf, downloadRowsAsCsv } from '@/utils/pdfExport'
import TimesheetEntryDetailModal from '@/components/timesheets/TimesheetEntryDetailModal.vue'
import TimesheetListToolbar from '@/components/timesheets/list/TimesheetListToolbar.vue'
import TimesheetProjectGroup from '@/components/timesheets/list/TimesheetProjectGroup.vue'
import TimesheetAddEntryModal from '@/components/timesheets/list/TimesheetAddEntryModal.vue'
import TimesheetClockOutModal from '@/components/timesheets/list/TimesheetClockOutModal.vue'
import TimesheetRejectModal from '@/components/timesheets/list/TimesheetRejectModal.vue'
import TimesheetEditLeaveModal from '@/components/timesheets/list/TimesheetEditLeaveModal.vue'
import TimesheetBulkHoursModal from '@/components/timesheets/list/TimesheetBulkHoursModal.vue'
import TimesheetInviteModal from '@/components/timesheets/list/TimesheetInviteModal.vue'
import TimesheetExportModal from '@/components/timesheets/list/TimesheetExportModal.vue'
import TimesheetPhotoLightbox from '@/components/timesheets/list/TimesheetPhotoLightbox.vue'

function splitIso(iso) {
  return splitBusinessIso(iso)
}

function combineDateTime(date, time) {
  return combineBusinessDateTime(date, time)
}

function emptyEditLeaveForm() {
  return {
    id: null,
    worker_id: null,
    project_id: null,
    leave_type: null,
    start_date: '',
    end_date: '',
    include_weekends: false,
    hours_per_day: null,
    note: '',
  }
}

function emptyAddEntryForm() {
  return {
    tradie_id: null,
    project_id: null,
    // null = Worked (a worked day is the absence of a leave_entries row, so
    // the API enum deliberately has no 'worked' member).
    leave_type: null,
    in_date: '',
    in_time: '',
    out_date: '',
    out_time: '',
    break_minutes: 0,
    // Multi-day folds the old separate "Apply Hours Across Days" modal in.
    multi_day: false,
    end_date: '',
    include_weekends: false,
    // Blank falls back to the company default for the leave type.
    leave_hours: null,
    comment: '',
    _locked_tradie: false,
    // [{ plant_equipment_id, quantity }] — only meaningful once clocked out;
    // see TimesheetAddEntryModal's showPlantSection.
    plant_entries: [],
  }
}

function emptyBulkHoursForm() {
  return {
    project_id: null,
    selected_ids: [],
    leave_type: null,
    start_date: '',
    end_date: '',
    include_weekends: false,
    start_time: '',
    finish_time: '',
    break_minutes: 0,
    leave_hours: null,
    comment: '',
  }
}

export default {
  name: 'TimesheetListTab',
  components: {
    TimesheetEntryDetailModal,
    TimesheetListToolbar,
    TimesheetProjectGroup,
    TimesheetAddEntryModal,
    TimesheetClockOutModal,
    TimesheetRejectModal,
    TimesheetEditLeaveModal,
    TimesheetBulkHoursModal,
    TimesheetInviteModal,
    TimesheetExportModal,
    TimesheetPhotoLightbox,
  },
  data() {
    return {
      loading: false,
      dayTick: 0,
      dayTickHandle: null,
      // Bumped on every fetchEvents() call so an in-flight request whose
      // response lands after a newer one (e.g. switching project filters
      // faster than the network responds) is dropped instead of overwriting
      // `events`/`leaveEntries` with stale, wrong-project data.
      fetchEventsSeq: 0,
      events: [],
      leaveEntries: [],
      projectTradies: [],
      allTradiesMap: {},
      selectedDays: {},
      dateFrom: '',
      dateTo: '',
      selectedProjectId: null,
      searchQuery: '',
      expandedTradies: {},
      collapsedProjects: {},
      collapsedInactive: {},
      inactiveSectionCollapsed: true,
      rowActivityFilter: 'all',
      rowActivityFilterOptions: [
        { value: 'all', text: 'All tradies' },
        { value: 'clocked_in', text: 'Clocked in' },
        { value: 'pending', text: 'Pending approval' },
        { value: 'has_entries', text: 'Has entries' },
        { value: 'no_entries', text: 'No entries' },
      ],
      addEntryForm: emptyAddEntryForm(),
      clockOutForm: {
        eventId: null,
        clocked_in_at_display: '',
        date: '',
        time: '',
        break_minutes: 0,
        comment: '',
      },
      editForm: {
        id: null,
        in_date: '',
        in_time: '',
        out_date: '',
        out_time: '',
        break_minutes: 0,
        comment: '',
      },
      // Which event's row is showing the inline edit form (replaces the old
      // edit-event-modal — see startEditEvent/saveEditEvent/cancelEditEvent).
      editingEventId: null,
      editLeaveForm: emptyEditLeaveForm(),
      bulkHoursForm: emptyBulkHoursForm(),
      bulkHoursWorkers: [],
      bulkHoursSubmitting: false,
      addEntrySubmitting: false,
      approvingAll: false,
      rejectForm: {
        eventId: null,
        adminComment: '',
      },
      lightboxPhotos: [],
      lightboxIndex: 0,
      inviteForm: { email: '', loading: false },
      exportTradieSearch: '',
      exportOptions: {
        dateFrom: '',
        dateTo: '',
        exportProjectId: null,
        tradieIds: [],
        statuses: ['clocked_in', 'clocked_out', 'approved', 'rejected'],
        leaveTypes: [
          'public_holiday',
          'personal_leave',
          'sick_leave',
          'annual_leave',
        ],
        columns: {
          date: true,
          tradieName: true,
          project: true,
          zone: true,
          startTime: true,
          endTime: true,
          hours: true,
          breakMinutes: false,
          status: true,
          comment: false,
          shift: true,
        },
        sortBy: 'date',
        format: 'csv',
      },
    }
  },
  computed: {
    ...mapGetters('auth', [
      'user',
      'hasPermission',
      'isProjectScoped',
      'supervisorProjectIds',
      'isSupervisorTradie',
    ]),
    ...mapGetters('timesheet', ['timesheetSettings']),

    weekLabel() {
      if (!this.dateFrom || !this.dateTo) return '-'
      return `${dayjs(this.dateFrom).format('D MMM')} – ${dayjs(
        this.dateTo,
      ).format('D MMM')}`
    },

    exportTradieOptions() {
      return this.tradieOptions
        .filter((o) => o.value != null)
        .slice()
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    filteredExportTradieOptions() {
      const q = this.exportTradieSearch.trim().toLowerCase()
      if (!q) return this.exportTradieOptions
      return this.exportTradieOptions.filter((opt) =>
        opt.text.toLowerCase().includes(q),
      )
    },

    filteredExportTradieColumns() {
      return splitIntoColumns(this.filteredExportTradieOptions)
    },

    exportProjectOptions() {
      return this.$store.getters['timesheet/projectOptions']
    },

    exportProjectLabel() {
      const opt = this.exportProjectOptions.find(
        (o) => o.value === this.exportOptions.exportProjectId,
      )
      return opt ? opt.text : 'All Projects'
    },

    // Action gating: "View timesheets" alone shows data only; write actions
    // require their own permission. (hasPermission fails open for legacy
    // payloads with no permissions array.)
    canCreateTimesheet() {
      return this.hasPermission('timesheets.create')
    },
    canEditTimesheet() {
      return this.hasPermission('timesheets.edit')
    },
    canDeleteTimesheet() {
      return this.hasPermission('timesheets.delete')
    },
    canDownloadTimesheet() {
      return this.hasPermission('timesheets.download')
    },
    activeFilterCount() {
      return this.rowActivityFilter === 'all' ? 0 : 1
    },
    showInactiveRows() {
      return (
        this.rowActivityFilter === 'all' ||
        this.rowActivityFilter === 'no_entries'
      )
    },

    today() {
      // bizNow() reads the system clock, not reactive Vue state, so without
      // this Vue would cache the result forever after the first access and
      // "today" would stay frozen at whichever day the tab was first opened.
      void this.dayTick
      return bizNow().format('YYYY-MM-DD')
    },
    // Bounded future window for BA/supervisor worked-hours entry — see
    // docs/designs/future-date-timesheet-entries.md. Passed to
    // TimesheetAddEntryModal/TimesheetBulkHoursModal and the inline
    // edit-event form's worked-hours date pickers only; leave stays capped
    // at `today` (no
    // "not yet worked" concept applies to leave), and
    // TimesheetClockOutModal stays capped at `today` (a future clock-out
    // has no coherent meaning). The API enforces the same 30-day bound
    // server-side (TimesheetZoneEvent::MAX_FUTURE_DAYS) — this is a UX
    // convenience, not the safety guarantee itself.
    maxFutureDate() {
      void this.dayTick
      return bizNow().add(30, 'day').format('YYYY-MM-DD')
    },
    projectOptions() {
      const active = this.$store.state.project.projects || []
      const archived = this.$store.state.project.archivedProjects || []
      const opts = [{ value: null, text: 'All Projects' }]
      if (active.length) {
        opts.push({
          label: 'Active',
          options: active.map(function (p) {
            return { value: p.id, text: p.name }
          }),
        })
      }
      if (archived.length) {
        opts.push({
          label: 'Archived',
          options: archived.map(function (p) {
            return { value: p.id, text: p.name }
          }),
        })
      }
      return opts
    },
    archivedProjectIds() {
      return new Set(
        (this.$store.state.project.archivedProjects || []).map(function (p) {
          return String(p.id)
        }),
      )
    },
    // Options for the modals' required project select — same project set the
    // page filter offers (scoped for supervisors), without the "All Projects"
    // entry.
    modalProjectOptions() {
      const projects = this.$store.getters['timesheet/projectOptions'].filter(
        function (o) {
          return o.value != null
        },
      )
      return [{ value: null, text: '- Select project -' }].concat(projects)
    },
    tradieOptions() {
      const seen = {}
      this.projectTradies.forEach(function (t) {
        if (t && t.id) seen[t.id] = t.full_name || t.name || 'Tradie #' + t.id
      })
      this.events.forEach(function (e) {
        if (e.tradie && e.tradie.id && !seen[e.tradie.id]) {
          seen[e.tradie.id] = e.tradie.name
        }
      })
      return [{ value: null, text: '- Select tradie -' }].concat(
        Object.keys(seen).map(function (id) {
          return { value: Number(id), text: seen[id] }
        }),
      )
    },
    weekDays() {
      const from = dayjs(this.dateFrom)
      const to = dayjs(this.dateTo)
      const todayStr = bizNow().format('YYYY-MM-DD')
      const days = []
      const totalDays = to.diff(from, 'day') + 1
      const limit = Math.min(totalDays, 35)
      for (let i = 0; i < limit; i++) {
        const d = from.add(i, 'day')
        const iso = d.format('YYYY-MM-DD')
        days.push({
          iso,
          short: d.format('ddd'),
          date: d.format('D MMM'),
          isToday: iso === todayStr,
        })
      }
      return days
    },
    leaveDayInstances() {
      const visibleDays = new Set(this.weekDays.map((day) => day.iso))
      const instances = []

      this.leaveEntries.forEach((entry) => {
        const tradieName = (entry.worker && entry.worker.full_name) || 'Unknown'
        let cursor = dayjs(entry.start_date)
        let guard = 0
        while (cursor.format('YYYY-MM-DD') <= entry.end_date && guard < 400) {
          const dayIso = cursor.format('YYYY-MM-DD')
          const isWeekend = cursor.day() === 0 || cursor.day() === 6
          if (
            visibleDays.has(dayIso) &&
            (entry.include_weekends || !isWeekend)
          ) {
            instances.push({
              workerId: entry.worker_id,
              tradieName,
              trade: entry.worker && entry.worker.trade,
              projectId: entry.project_id || 'unassigned',
              dayIso,
              event: this.syntheticLeaveEvent(entry, dayIso),
            })
          }
          cursor = cursor.add(1, 'day')
          guard += 1
        }
      })

      return instances
    },
    singleProjectGroup() {
      const projects = (this.$store.state.project.projects || []).concat(
        this.$store.state.project.archivedProjects || [],
      )
      const project = projects.find(
        (item) => String(item.id) === String(this.selectedProjectId),
      )
      return {
        projectId: this.selectedProjectId,
        projectName: project ? project.name : 'Project',
        isArchived: this.archivedProjectIds.has(String(this.selectedProjectId)),
        tradies: this.groupedByProject.tradies,
        inactiveTradies: this.groupedByProject.inactiveTradies,
      }
    },
    // Single-project view: one flat group of the project's tradies. Zones are
    // a geofence/compliance layer now — events carry a per-entry
    // within_zone_in check instead of driving the grouping.
    groupedByProject() {
      const tradieAgg = {}
      const inactiveMap = {}

      this.projectTradies.forEach(function (t) {
        if (!t || !t.id) return
        inactiveMap[t.id] = emptyTradieRow(
          t.id,
          t.full_name || t.name || 'Tradie #' + t.id,
          t.trade,
        )
      })

      // Aggregate every event onto its tradie. A tradie can have a mix of
      // GPS events and manual/supervisor entries — they all merge into the
      // one row for the selected project.
      this.events.forEach(function (e) {
        const tradieId = (e.tradie && e.tradie.id) || 'unknown'
        const tradieName = (e.tradie && e.tradie.name) || 'Unknown'

        delete inactiveMap[tradieId]

        if (!tradieAgg[tradieId])
          tradieAgg[tradieId] = emptyTradieRow(
            tradieId,
            tradieName,
            e.tradie && e.tradie.trade,
          )

        applyEventToTradieRow(tradieAgg[tradieId], e, e.timezone)
      })

      this.leaveDayInstances.forEach(function (instance) {
        delete inactiveMap[instance.workerId]
        if (!tradieAgg[instance.workerId]) {
          tradieAgg[instance.workerId] = emptyTradieRow(
            instance.workerId,
            instance.tradieName,
            instance.trade,
          )
        }
        const row = tradieAgg[instance.workerId]
        if (!row.eventsByDay[instance.dayIso])
          row.eventsByDay[instance.dayIso] = []
        row.eventsByDay[instance.dayIso].push(instance.event)
        row.totalEntries += 1
      })

      let tradies = Object.values(tradieAgg).sort(function (a, b) {
        return a.tradieName.localeCompare(b.tradieName)
      })
      let inactiveTradies = Object.values(inactiveMap).sort(function (a, b) {
        return a.tradieName.localeCompare(b.tradieName)
      })

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase()
        tradies = tradies.filter(function (t) {
          return t.tradieName.toLowerCase().indexOf(q) !== -1
        })
        inactiveTradies = inactiveTradies.filter(function (t) {
          return t.tradieName.toLowerCase().indexOf(q) !== -1
        })
      }

      return { tradies, inactiveTradies }
    },
    projectGroups() {
      const projectMap = {}
      const self = this

      // Seed all rostered tradies into their projects (no events yet — they
      // go into inactiveMap)
      Object.keys(this.allTradiesMap).forEach(function (pid) {
        const entry = self.allTradiesMap[pid]
        projectMap[pid] = {
          projectId: pid,
          projectName: entry.projectName,
          isArchived: self.archivedProjectIds.has(String(pid)),
          inactiveMap: {},
        }
        entry.tradies.forEach(function (t) {
          if (!t || !t.id) return
          projectMap[pid].inactiveMap[t.id] = emptyTradieRow(
            t.id,
            t.full_name || t.name || 'Tradie #' + t.id,
            t.trade,
          )
        })
      })

      // Aggregate every event onto its (project, tradie) pair — see
      // groupedByProject() for why GPS and manual/supervisor entries must
      // merge into a single row per tradie.
      const tradieAggByProject = {}
      this.events.forEach(function (e) {
        const pid = e.project_id || 'unassigned'
        // Top-level `project` is authoritative (keyed off the event's own
        // project_id); zone.project only reflects the matched zone.
        const pname =
          (e.project && e.project.name) ||
          (e.zone && e.zone.project && e.zone.project.name) ||
          'Unassigned'
        const tradieId = (e.tradie && e.tradie.id) || 'unknown'
        const tradieName = (e.tradie && e.tradie.name) || 'Unknown'

        if (!projectMap[pid])
          projectMap[pid] = {
            projectId: pid,
            projectName: pname,
            isArchived: self.archivedProjectIds.has(String(pid)),
            inactiveMap: {},
          }
        if (!tradieAggByProject[pid]) tradieAggByProject[pid] = {}

        // Move tradie out of inactive if they have events
        delete projectMap[pid].inactiveMap[tradieId]

        const byTradie = tradieAggByProject[pid]
        if (!byTradie[tradieId])
          byTradie[tradieId] = emptyTradieRow(
            tradieId,
            tradieName,
            e.tradie && e.tradie.trade,
          )

        applyEventToTradieRow(byTradie[tradieId], e, e.timezone)
      })

      this.leaveDayInstances.forEach(function (instance) {
        const pid = instance.projectId
        if (!projectMap[pid]) {
          projectMap[pid] = {
            projectId: pid,
            projectName: self.resolveProjectName(pid),
            isArchived: self.archivedProjectIds.has(String(pid)),
            inactiveMap: {},
          }
        }
        if (!tradieAggByProject[pid]) tradieAggByProject[pid] = {}

        delete projectMap[pid].inactiveMap[instance.workerId]
        const byTradie = tradieAggByProject[pid]
        if (!byTradie[instance.workerId]) {
          byTradie[instance.workerId] = emptyTradieRow(
            instance.workerId,
            instance.tradieName,
            instance.trade,
          )
        }
        const row = byTradie[instance.workerId]
        if (!row.eventsByDay[instance.dayIso])
          row.eventsByDay[instance.dayIso] = []
        row.eventsByDay[instance.dayIso].push(instance.event)
        row.totalEntries += 1
      })

      let groups = Object.values(projectMap)
        .sort(function (a, b) {
          if (a.isArchived !== b.isArchived) return a.isArchived ? 1 : -1
          return a.projectName.localeCompare(b.projectName)
        })
        .map(function (g) {
          return {
            projectId: g.projectId,
            projectName: g.projectName,
            isArchived: g.isArchived,
            tradies: Object.values(tradieAggByProject[g.projectId] || {}).sort(
              function (a, b) {
                return a.tradieName.localeCompare(b.tradieName)
              },
            ),
            inactiveTradies: Object.values(g.inactiveMap).sort(function (a, b) {
              return a.tradieName.localeCompare(b.tradieName)
            }),
          }
        })
        .filter(function (g) {
          return g.tradies.length > 0 || g.inactiveTradies.length > 0
        })

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase()
        groups = groups
          .map(function (g) {
            return {
              projectId: g.projectId,
              projectName: g.projectName,
              isArchived: g.isArchived,
              tradies: g.tradies.filter(function (t) {
                return t.tradieName.toLowerCase().indexOf(q) !== -1
              }),
              inactiveTradies: g.inactiveTradies.filter(function (t) {
                return t.tradieName.toLowerCase().indexOf(q) !== -1
              }),
            }
          })
          .filter(function (g) {
            return g.tradies.length > 0 || g.inactiveTradies.length > 0
          })
      }

      return groups
    },
  },
  watch: {
    // Pay-period settings can change on another tab (the Dashboard settings
    // card) while this one stays mounted. Re-anchor the range when the
    // schedule actually changes — ignore the first null → object load and
    // leave-defaults-only saves.
    timesheetSettings(newVal, oldVal) {
      if (!oldVal || !newVal) return
      const changed =
        oldVal.pay_period_weeks !== newVal.pay_period_weeks ||
        oldVal.pay_period_anchor_date !== newVal.pay_period_anchor_date ||
        oldVal.start_day !== newVal.start_day
      if (!changed) return
      this.applyCurrentWeek()
      this.fetchEvents()
    },
  },
  beforeDestroy() {
    if (this.dayTickHandle) window.clearInterval(this.dayTickHandle)
  },
  async mounted() {
    this.dayTickHandle = window.setInterval(() => {
      this.dayTick++
    }, 60 * 1000)
    this.loading = true
    // Cached after first load — cheap. Must resolve before applyCurrentWeek()
    // so the default week honours the business's configured start day.
    await this.$store.dispatch('timesheet/getTimesheetSettings')
    // Per-project pay-period overrides so the current-week reset honours a
    // project on a different week. Cached; failure falls back to company.
    // Fired here (in parallel with getProjects below) but not awaited until
    // after the default project is chosen — applyCurrentWeek() needs both
    // this data AND the final selectedProjectId before it computes the
    // range, otherwise it silently anchors to the company default even when
    // the eventual project has its own override.
    const projectPayPeriodsPromise = this.$store.dispatch(
      'timesheet/getProjectPayPeriods',
    )
    try {
      await Promise.all([
        this.$store.dispatch('project/getProjects', {
          limit: 100,
          is_archived: false,
        }),
        this.$store.dispatch('project/getArchivedProjects'),
      ])
    } catch (e) {}
    // Default to the first available project from the freshly-loaded options
    // (getProjects is awaited above) rather than the possibly-stale auth
    // assigned_project_ids. Admins keep their "All Projects" (null) default.
    if (!this.selectedProjectId) {
      const hasAllProjects = this.projectOptions.some(
        (o) => o.value == null && !o.disabled,
      )
      const firstProject = this.projectOptions.find(
        (o) => o.value != null && !o.disabled,
      )
      if (!hasAllProjects && firstProject) {
        this.selectedProjectId = firstProject.value
        const cached = this.allTradiesMap[this.selectedProjectId]
        this.projectTradies = cached ? cached.tradies : []
      }
    }
    await projectPayPeriodsPromise
    this.applyCurrentWeek()
    await this.loadAllProjectTradies()
    try {
      const zoneParams =
        this.isProjectScoped && this.supervisorProjectIds.length > 0
          ? { project_ids: this.supervisorProjectIds }
          : {}
      await this.$store.dispatch('timesheet/getZones', zoneParams)
    } catch (e) {}
    await this.fetchEvents()
  },
  methods: {
    resolveProjectName(projectId) {
      const projects = (this.$store.state.project.projects || []).concat(
        this.$store.state.project.archivedProjects || [],
      )
      const project = projects.find(
        (item) => String(item.id) === String(projectId),
      )
      return project ? project.name : 'Unassigned'
    },
    syntheticLeaveEvent(entry, dayIso) {
      return {
        id: 'leave-' + entry.id + '-' + dayIso,
        is_leave: true,
        leave_id: entry.id,
        leave_type: entry.leave_type,
        leave_note: entry.note,
        leave_hours_per_day: entry.hours_per_day,
        leave_resolved_hours: entry.resolved_hours,
        leave_start_date: entry.start_date,
        leave_end_date: entry.end_date,
        leave_date: dayIso,
        leave_worker_id: entry.worker_id,
        leave_project_id: entry.project_id,
        leave_include_weekends: entry.include_weekends,
        tradie: entry.worker
          ? {
              id: entry.worker.id,
              name: entry.worker.full_name,
              trade: entry.worker.trade,
            }
          : null,
        project: {
          id: entry.project_id || null,
          name: this.resolveProjectName(entry.project_id),
        },
      }
    },
    visibleTradies(tradies) {
      if (this.rowActivityFilter === 'clocked_in') {
        return tradies.filter((r) => r.activeEvent)
      }
      if (this.rowActivityFilter === 'pending') {
        return tradies.filter((r) => r.pendingCount > 0)
      }
      if (this.rowActivityFilter === 'has_entries') {
        return tradies.filter((r) => r.totalEntries > 0)
      }
      if (this.rowActivityFilter === 'no_entries') {
        return tradies.filter((r) => r.totalEntries === 0)
      }
      return tradies
    },
    // "All Projects" fetches the widest span across every project (see
    // applyCurrentWeek()) so every project's data is covered in one request.
    // Each project group renders only ITS OWN slice of that shared window —
    // a project with no override (or a shorter override) shouldn't show
    // days that belong to some other, longer-period project alongside it.
    weekDaysForProject(projectId) {
      if (this.selectedProjectId) return this.weekDays
      var schedule =
        this.$store.getters['timesheet/projectPayPeriod'](projectId)
      if (!schedule) return this.weekDays
      // Trim to the project's own period LENGTH (not its current_period's
      // absolute dates, which are always pinned to today's period) so a
      // navigated-to previous/future period still renders its full days
      // instead of filtering everything out against "today".
      var periodDays = schedule.pay_period_weeks
        ? schedule.pay_period_weeks * 7
        : null
      if (!periodDays) {
        var cp = schedule.current_period
        if (cp && cp.start_date && cp.end_date) {
          // daySpan() is already inclusive (same day = 1) - see
          // utils/weekHelpers.js - so no extra +1 here.
          periodDays = daySpan(cp.start_date, cp.end_date)
        }
      }
      if (!periodDays) return this.weekDays
      return this.weekDays.slice(0, periodDays)
    },
    applyCurrentWeek() {
      // "All Projects": no single project's schedule applies to the whole
      // page, so fetch the UNION of every visible project's own current
      // period (its override, or the company default if it has none) — the
      // widest span covers every project's data in one request. Each
      // project's group then trims its own rendered days back down to its
      // own period via weekDaysForProject(), so a 1-week project still shows
      // 7 days even though a 2-week project alongside it fetched 14.
      if (!this.selectedProjectId) {
        var getPeriod = this.$store.getters['timesheet/projectPayPeriod']
        var periods = (this.$store.state.project.projects || [])
          .map(function (p) {
            var schedule = getPeriod(p.id)
            return schedule && schedule.current_period
          })
          .filter(function (cp) {
            return cp && cp.start_date && cp.end_date
          })
        if (periods.length) {
          this.dateFrom = periods
            .map(function (p) {
              return p.start_date
            })
            .reduce(function (a, b) {
              return a < b ? a : b
            })
          this.dateTo = periods
            .map(function (p) {
              return p.end_date
            })
            .reduce(function (a, b) {
              return a > b ? a : b
            })
          return
        }
      }
      // Use the resolved current pay period for the selected scope (its
      // override, or the company default for "All Projects") — this is the
      // full span, so a fortnightly schedule fills 14 days, not 7.
      var schedule = this.$store.getters['timesheet/projectPayPeriod'](
        this.selectedProjectId,
      )
      var cp = schedule && schedule.current_period
      if (cp && cp.start_date && cp.end_date) {
        this.dateFrom = cp.start_date
        this.dateTo = cp.end_date
        return
      }
      // Fallback (settings not loaded yet): align a 7-day week to the start day.
      var startDay = this.$store.getters['timesheet/projectStartDay'](
        this.selectedProjectId,
      )
      var weekStart = startDayOf(bizNow().format('YYYY-MM-DD'), startDay)
      this.dateFrom = weekStart
      this.dateTo = dayjs(weekStart).add(6, 'day').format('YYYY-MM-DD')
    },
    resetToCurrentWeek() {
      this.applyCurrentWeek()
      this.fetchEvents()
    },
    onDateFromChange() {
      if (this.dateFrom) {
        this.dateTo = dayjs(this.dateFrom).add(6, 'day').format('YYYY-MM-DD')
      }
      this.fetchEvents()
    },
    shiftWeek(direction) {
      // Step by the length of the current range, so prev/next moves a whole
      // pay period (7, 14, … days) rather than always a single week.
      const hasBoth = !!(this.dateFrom && this.dateTo)
      const from = this.dateFrom || this.dateTo || bizNow().format('YYYY-MM-DD')
      const to = this.dateTo || this.dateFrom || bizNow().format('YYYY-MM-DD')
      const span = hasBoth ? daySpan(from, to) : 7
      this.dateFrom = addDays(from, direction * span)
      this.dateTo = addDays(to, direction * span)
      this.fetchEvents()
    },
    selectDay(key, dayIso) {
      const current = this.selectedDays[key]
      this.$set(this.selectedDays, key, current === dayIso ? null : dayIso)
    },
    getSelectedDay(key) {
      return this.selectedDays[key] || null
    },
    toggleExpand(key) {
      if (this.expandedTradies[key]) {
        this.$delete(this.expandedTradies, key)
      } else {
        this.$set(this.expandedTradies, key, true)
      }
    },
    toggleProject(projectId) {
      this.$set(
        this.collapsedProjects,
        projectId,
        !this.collapsedProjects[projectId],
      )
    },
    isInactiveCollapsed(projectId) {
      return this.collapsedInactive[projectId] !== false
    },
    toggleInactiveSection(projectId) {
      if (this.collapsedInactive[projectId] === false) {
        this.$delete(this.collapsedInactive, projectId)
      } else {
        this.$set(this.collapsedInactive, projectId, false)
      }
    },
    isExpanded(tradieId) {
      return !!this.expandedTradies[tradieId]
    },
    async loadAllProjectTradies() {
      const active = this.$store.state.project.projects || []
      const archived = this.$store.state.project.archivedProjects || []
      const allProjects = active.concat(archived)
      if (!allProjects.length) return
      // Single batched call for every project instead of one GET
      // /projects/{id} per project — see project/getAllProjectTradies.
      const byProject = await this.$store.dispatch(
        'project/getAllProjectTradies',
      )
      const newMap = {}
      byProject.forEach(function (p) {
        newMap[p.project_id] = {
          projectName: p.project_name,
          tradies: p.tradies || [],
        }
      })
      this.allTradiesMap = newMap
    },
    onProjectChange() {
      const cached = this.allTradiesMap[this.selectedProjectId]
      this.projectTradies = cached ? cached.tradies : []
      // Re-anchor the week range to the selected project's pay-period week
      // start (its override, or the company default for "All Projects").
      this.applyCurrentWeek()
      this.fetchEvents()
    },
    async fetchEvents() {
      if (this.isProjectScoped && !this.selectedProjectId) return
      const seq = ++this.fetchEventsSeq
      this.loading = true
      this.$emit('loading', true)
      try {
        const params = {
          date_from: this.dateFrom,
          date_to: this.dateTo,
          per_page: 100,
        }
        if (this.selectedProjectId) params.project_id = this.selectedProjectId
        const leaveParams = {
          date_from: this.dateFrom,
          date_to: this.dateTo,
        }
        if (this.selectedProjectId)
          leaveParams.project_id = this.selectedProjectId
        const results = await Promise.allSettled([
          this.$store.dispatch('timesheet/getEvents', params),
          this.$store.dispatch('timesheet/getLeaveEntries', leaveParams),
        ])
        // A newer fetchEvents() call has since started (e.g. the user
        // switched project/date filters again) — its response will apply
        // instead, so drop this now-stale one rather than overwrite fresher
        // state with data for a filter the user has already navigated away
        // from.
        if (seq !== this.fetchEventsSeq) return
        if (results[0].status === 'rejected') throw results[0].reason
        this.events = results[0].value || []
        this.leaveEntries =
          results[1].status === 'fulfilled' ? results[1].value || [] : []
        if (results[1].status === 'rejected') {
          this.$bvToast.toast(
            'Timesheets loaded, but leave could not be shown.',
            {
              title: 'Leave unavailable',
              variant: 'warning',
              solid: true,
            },
          )
        }
      } catch (e) {
        if (seq !== this.fetchEventsSeq) return
        this.$bvToast.toast('Failed to load timesheet events.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        if (seq === this.fetchEventsSeq) {
          this.loading = false
          this.$emit('loading', false)
        }
      }
    },
    async approveEvent(eventId, decision, adminComment) {
      try {
        await this.$store.dispatch('timesheet/approveEvent', {
          eventId,
          decision,
          adminComment,
        })
        await this.confirmIfPending(eventId)
        this.$bvToast.toast(
          decision === 'approved'
            ? 'Timesheet approved.'
            : 'Timesheet rejected.',
          {
            title: 'Success',
            variant: decision === 'approved' ? 'success' : 'warning',
            solid: true,
          },
        )
        this.fetchEvents()
      } catch (e) {
        this.$bvToast.toast('Action failed.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    // A pending_confirmation entry needs its confirmation cleared before it
    // will read as Approved/Rejected instead of "Pending confirmation" — see
    // eventStatusLabel() in timesheetHelpers.js and
    // docs/designs/future-date-timesheet-entries.md. Approve/Reject fold that
    // step in automatically so one click is enough; failures here are
    // swallowed since the approve/reject decision itself already succeeded.
    async confirmIfPending(eventId) {
      var item = this.events.find(function (e) {
        return e.id === eventId
      })
      if (item && item.confirmation_status === 'pending_confirmation') {
        try {
          await this.$store.dispatch('timesheet/confirmEvent', eventId)
        } catch (e) {
          // approve/reject already succeeded; confirmation will just retry
          // next time the row is actioned
        }
      }
    },

    detailApprove(eventId) {
      this.approveEvent(eventId, 'approved')
    },
    detailReject(payload) {
      this.approveEvent(payload.eventId, 'rejected', payload.adminComment)
    },

    // Without this guard, a double-click (or a click while the bulk request
    // is still in flight) fires this handler twice concurrently — same
    // class of bug as submitAddEntry's addEntrySubmitting guard.
    async approveEvents(events) {
      if (!events.length) return
      if (this.approvingAll) return
      this.approvingAll = true
      try {
        const eventIds = events.map(function (e) {
          return e.id
        })
        const result = await this.$store.dispatch(
          'timesheet/approveEventsBulk',
          { eventIds: eventIds, decision: 'approved' },
        )
        const approved = (result && result.approved) || []
        const failed = (result && result.failed) || []
        const total = eventIds.length
        if (failed.length === 0) {
          this.$bvToast.toast(
            total + ' ' + (total === 1 ? 'entry' : 'entries') + ' approved.',
            { title: 'Success', variant: 'success', solid: true },
          )
        } else if (approved.length > 0) {
          this.$bvToast.toast(
            approved.length +
              ' of ' +
              total +
              ' approved. ' +
              failed.length +
              ' could not be approved.',
            { title: 'Partial success', variant: 'warning', solid: true },
          )
        } else {
          this.$bvToast.toast('Approval failed.', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          })
        }
        this.fetchEvents()
      } catch (e) {
        this.$bvToast.toast('Approval failed.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.approvingAll = false
      }
    },
    approveAllForRow(row) {
      this.approveEvents(getApprovableEvents(row))
    },
    // Project-level "Approve all pending" (Phase 3 — bulk approve beyond a
    // single row). Reuses the exact same per-event approve dispatch as
    // approveAllForRow, just gathered across every worker in the group.
    approveAllForGroup(group) {
      const events = (group.tradies || []).reduce(
        (all, row) => all.concat(getApprovableEvents(row)),
        [],
      )
      this.approveEvents(events)
    },

    openAddEntryModal(row, projectId, dayIso) {
      const form = emptyAddEntryForm()
      if (row && row.tradieId !== 'unknown') {
        form.tradie_id = row.tradieId
        form._locked_tradie = true
      }
      form.in_date = dayIso || bizNow().format('YYYY-MM-DD')
      form.in_time = bizNow().format('HH:mm')
      form.project_id = projectId || this.selectedProjectId || null
      this.addEntryForm = form
      this.$bvModal.show('add-entry-modal')
    },
    handleDayClick(row, projectId, key, day) {
      const hasEvents =
        row.eventsByDay[day.iso] && row.eventsByDay[day.iso].length
      if (hasEvents) {
        this.selectDay(key, day.iso)
      } else if (this.canCreateTimesheet) {
        this.openAddEntryModal(row, projectId, day.iso)
      }
    },
    handleDayClickPayload(payload) {
      this.handleDayClick(
        payload.row,
        payload.projectId,
        payload.rowKey,
        payload.day,
      )
    },
    resetAddEntryForm() {
      this.addEntryForm = emptyAddEntryForm()
    },
    // One modal, three destinations. Leave lives in leave_entries (which is
    // range-native, so a single day is just start_date === end_date); worked
    // days are timesheet events, which need the bulk endpoint for a range.
    async submitAddEntry(bvModalEvent) {
      bvModalEvent.preventDefault()
      // Without this guard, a double-click (or Enter + a click) on the OK
      // button fires this handler twice before the first request resolves —
      // the modal's ok-disabled only checks form validity, not in-flight
      // state — creating two identical timesheet entries for the same
      // tradie/day. See TimesheetBulkHoursModal/bulkHoursSubmitting for the
      // same pattern.
      if (this.addEntrySubmitting) return
      this.addEntrySubmitting = true
      const form = this.addEntryForm
      try {
        let message
        if (form.leave_type) {
          message = await this.createLeaveFromAddEntry(form)
        } else if (form.multi_day) {
          message = await this.createBulkFromAddEntry(form)
        } else {
          message = await this.createSingleFromAddEntry(form)
        }
        this.$bvModal.hide('add-entry-modal')
        this.$bvToast.toast(message, {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
        this.fetchEvents()
      } catch (e) {
        this.$bvToast.toast(
          this.clockInErrorMessage(e, 'Failed to add entry.'),
          {
            title: 'Error',
            variant: 'danger',
            solid: true,
          },
        )
      } finally {
        this.addEntrySubmitting = false
      }
    },

    // Note the field-name shift: leave_entries speaks worker_id/note, the
    // timesheet-event endpoints speak tradie_id/comment.
    async createLeaveFromAddEntry(form) {
      await this.$store.dispatch('timesheet/createLeaveEntry', {
        worker_id: form.tradie_id,
        project_id: form.project_id,
        leave_type: form.leave_type,
        start_date: form.in_date,
        end_date: form.multi_day ? form.end_date : form.in_date,
        // A single-day entry is one date the user explicitly picked, so it
        // always covers that date — sending false made a Saturday or Sunday
        // entry expand to zero days server-side (LeaveEntry::datesInScope),
        // which saved the row and then showed it nowhere. Only a multi-day
        // range has a real weekends-or-not question to answer, which is why
        // the toggle is rendered for that case alone.
        include_weekends: form.multi_day ? form.include_weekends : true,
        // null means "use the company default for this leave type", which the
        // API resolves from company_timesheet_settings.
        hours_per_day:
          form.leave_hours === '' || form.leave_hours === null
            ? null
            : form.leave_hours,
        note: form.comment || undefined,
      })
      const label = leaveTypeLabel(form.leave_type) || 'Leave'
      return `${label} added.`
    },

    async createBulkFromAddEntry(form) {
      const result = await this.$store.dispatch('timesheet/manualBulkEntry', {
        tradie_id: form.tradie_id,
        project_id: form.project_id,
        start_date: form.in_date,
        end_date: form.end_date,
        start_time: form.in_time,
        finish_time: form.out_time,
        break_minutes: form.break_minutes || 0,
        include_weekends: form.include_weekends,
        comment: form.comment || undefined,
      })
      const skippedCount = (result.skipped || []).length
      const days = (n) => `${n} day${n === 1 ? '' : 's'}`
      return skippedCount > 0
        ? `Added ${days(result.created_count)}. Skipped ${days(
            skippedCount,
          )} that already had an entry.`
        : `Added ${days(result.created_count)}.`
    },

    async createSingleFromAddEntry(form) {
      // Send the full entry (in + optional out) in a single create call so
      // "Add Entry" only needs timesheets.create, not edit (clock-out).
      // plant_entries only has somewhere to attach once clocked out — the API
      // rejects it otherwise — so it rides along with clocked_out_at, not
      // independently.
      const plantEntries = form.out_date
        ? (form.plant_entries || []).filter(
            (e) => e.plant_equipment_id && e.quantity != null,
          )
        : []
      await this.$store.dispatch('timesheet/manualClockIn', {
        tradie_id: form.tradie_id,
        project_id: form.project_id,
        clocked_in_at: combineDateTime(form.in_date, form.in_time),
        clocked_out_at: form.out_date
          ? combineDateTime(form.out_date, form.out_time)
          : undefined,
        break_minutes: form.out_date ? form.break_minutes || 0 : undefined,
        comment: form.comment || undefined,
        plant_entries: plantEntries.length ? plantEntries : undefined,
      })
      return 'Timesheet entry added.'
    },

    // Group-wide bulk hours entry (Timesheets tab "+ Add Hours") — applies
    // the same range/hours to multiple selected workers in one action.
    openBulkHoursModal(group) {
      const form = emptyBulkHoursForm()
      form.project_id = group.projectId
      form.start_date = this.today
      form.end_date = this.today
      this.bulkHoursForm = form

      const seen = {}
      ;(group.tradies || [])
        .concat(group.inactiveTradies || [])
        .forEach(function (t) {
          if (t && t.tradieId != null) seen[t.tradieId] = t
        })
      this.bulkHoursWorkers = Object.values(seen).sort(function (a, b) {
        return a.tradieName.localeCompare(b.tradieName)
      })

      this.$bvModal.show('bulk-hours-modal')
    },
    resetBulkHoursForm() {
      this.bulkHoursForm = emptyBulkHoursForm()
    },
    async submitBulkHours(bvModalEvent) {
      bvModalEvent.preventDefault()
      if (this.bulkHoursSubmitting) return
      this.bulkHoursSubmitting = true
      const form = this.bulkHoursForm
      let skippedTotal = 0
      const failed = []
      const noOpWorkers = []

      // One request for every selected worker — the API applies the range
      // to all of tradie_ids/worker_ids server-side in a single call and
      // reports each worker's outcome independently (see manualBulkEntry /
      // LeaveEntryController::storeBulk), instead of the client firing one
      // create request per worker.
      try {
        if (form.leave_type) {
          const results = await this.$store.dispatch(
            'timesheet/createLeaveEntryBulk',
            {
              worker_ids: form.selected_ids,
              project_id: form.project_id,
              leave_type: form.leave_type,
              start_date: form.start_date,
              end_date: form.end_date,
              include_weekends: form.include_weekends,
              hours_per_day:
                form.leave_hours === '' || form.leave_hours === null
                  ? null
                  : form.leave_hours,
              note: form.comment || undefined,
            },
          )
          results
            .filter((r) => r.status !== 'created')
            .forEach((r) => {
              const worker = this.bulkHoursWorkers.find(
                (w) => w.tradieId === r.worker_id,
              )
              failed.push({
                name: worker ? worker.tradieName : `Worker #${r.worker_id}`,
                message: r.message || 'Failed to add leave.',
              })
            })
        } else {
          const response = await this.$store.dispatch(
            'timesheet/manualBulkEntry',
            {
              tradie_ids: form.selected_ids,
              project_id: form.project_id,
              start_date: form.start_date,
              end_date: form.end_date,
              start_time: form.start_time,
              finish_time: form.finish_time,
              break_minutes: form.break_minutes || 0,
              include_weekends: form.include_weekends,
              comment: form.comment || undefined,
            },
          )
          ;(response.results || []).forEach((r) => {
            if (r.status === 'created') {
              skippedTotal += (r.skipped || []).length
              // created_count === 0 means every requested day for this
              // worker already had an entry on this project - nothing was
              // actually added, so this worker still won't show as clocked
              // in. Folding this into the generic success toast (as before)
              // hid that from the BA; call it out by name instead.
              if (!r.created_count) {
                const worker = this.bulkHoursWorkers.find(
                  (w) => w.tradieId === r.tradie_id,
                )
                noOpWorkers.push(
                  worker ? worker.tradieName : `Worker #${r.tradie_id}`,
                )
              }
              return
            }
            const worker = this.bulkHoursWorkers.find(
              (w) => w.tradieId === r.tradie_id,
            )
            failed.push({
              name: worker ? worker.tradieName : `Worker #${r.tradie_id}`,
              message: this.bulkResultErrorMessage(r, 'Failed to add hours.'),
            })
          })
        }
      } catch (e) {
        // Request-level failure (network error, or a validation error that
        // covers the whole batch, e.g. an out-of-range date) — every
        // selected worker failed the same way.
        form.selected_ids.forEach((tradieId) => {
          const worker = this.bulkHoursWorkers.find(
            (w) => w.tradieId === tradieId,
          )
          failed.push({
            name: worker ? worker.tradieName : `Worker #${tradieId}`,
            message: this.clockInErrorMessage(e, 'Failed to add hours.'),
          })
        })
      }

      this.bulkHoursSubmitting = false
      this.$bvModal.hide('bulk-hours-modal')
      this.fetchEvents()

      const successCount =
        form.selected_ids.length - failed.length - noOpWorkers.length
      if (!failed.length) {
        const skippedNote =
          skippedTotal > 0
            ? ` ${skippedTotal} day${
                skippedTotal === 1 ? '' : 's'
              } skipped where an entry already existed.`
            : ''
        const noOpNote = noOpWorkers.length
          ? ` No new hours for ${noOpWorkers.join(
              ', ',
            )} - they already had an entry every selected day.`
          : ''
        this.$bvToast.toast(
          `Hours added for ${successCount} worker${
            successCount === 1 ? '' : 's'
          }.${skippedNote}${noOpNote}`,
          {
            title: noOpWorkers.length ? 'Some entries skipped' : 'Success',
            variant: noOpWorkers.length ? 'warning' : 'success',
            solid: true,
          },
        )
      } else {
        // One bulk request applies the same start/finish/date range to every
        // selected worker, so a range-level mistake (e.g. finish time before
        // start time) fails every worker with the identical message. Group
        // by message so that shows up as one line, not the same sentence
        // repeated per name.
        const failedMessage = this.groupFailuresByMessage(failed)
        this.$bvToast.toast(
          `Added for ${successCount} worker${
            successCount === 1 ? '' : 's'
          }. Failed for ${failed.length}: ${failedMessage}`,
          { title: 'Some entries failed', variant: 'danger', solid: true },
        )
      }
    },

    // Collapses a list of {name, message} failures into one line per unique
    // message: "<message> (Name1, Name2)" — or just the message when every
    // failure shares it, since naming all of them adds nothing when the
    // cause is a single range-level mistake (see submitBulkHours above).
    groupFailuresByMessage(failed) {
      const order = []
      const byMessage = new Map()
      failed.forEach((f) => {
        if (!byMessage.has(f.message)) {
          byMessage.set(f.message, [])
          order.push(f.message)
        }
        byMessage.get(f.message).push(f.name)
      })
      if (order.length === 1) return order[0]
      return order
        .map((message) => `${message} (${byMessage.get(message).join(', ')})`)
        .join('; ')
    },

    // The API 409s with error_code "active_clock_in" when the tradie already
    // has an open clock-in (one-open-event-per-tradie rule); surface the
    // server's message plus the active site label when present.
    clockInErrorMessage(e, fallback = 'Clock in failed.') {
      const data = e && e.response && e.response.data
      if (data && data.error_code === 'active_clock_in') {
        return data.active_label
          ? `This tradie is already clocked in at ${data.active_label}.`
          : data.message || 'This tradie is already clocked in.'
      }
      return (data && data.message) || fallback
    },

    // Same "already clocked in at X" phrasing as clockInErrorMessage(), but
    // for one item of a manualBulkEntry tradie_ids response — the fields
    // sit directly on the result object rather than under e.response.data,
    // since this is a per-worker outcome inside a 200, not a request error.
    bulkResultErrorMessage(result, fallback) {
      if (result.error_code === 'active_clock_in') {
        return result.active_label
          ? `This tradie is already clocked in at ${result.active_label}.`
          : result.message || 'This tradie is already clocked in.'
      }
      return result.message || fallback
    },

    openClockOutModal(event) {
      // Display-only: the existing clock-in reads in the timezone it was
      // stamped with. The seeded date/time below feeds adminClockOut(),
      // whose naive-string parsing the API deliberately left un-timezone-
      // aware this phase (docs/designs/per-site-timezones.md, Phase 1
      // deploy 1b scope note) — it still expects Perth wall-clock, so this
      // stays on BUSINESS_TZ (bizNow() with no tz arg) until that API path
      // is updated. Only editEvent()/adminClockOut()'s WRITE side is
      // exempt; everything read-only is timezone-aware from this phase.
      this.clockOutForm.eventId = event.id
      this.clockOutForm.clocked_in_at_display = event.clocked_in_at
        ? bizFromIso(event.clocked_in_at, event.timezone).format(
            'D MMM YYYY, h:mm a',
          )
        : ''
      this.clockOutForm.date = bizNow().format('YYYY-MM-DD')
      this.clockOutForm.time = bizNow().format('HH:mm')
      this.clockOutForm.break_minutes = 0
      this.clockOutForm.comment = event.comment || ''
      this.$bvModal.show('clock-out-modal')
    },
    resetClockOutForm() {
      this.clockOutForm = {
        eventId: null,
        clocked_in_at_display: '',
        date: '',
        time: '',
        break_minutes: 0,
        comment: '',
      }
    },
    async submitClockOut(bvModalEvent) {
      bvModalEvent.preventDefault()
      try {
        await this.$store.dispatch('timesheet/adminClockOut', {
          eventId: this.clockOutForm.eventId,
          clocked_out_at: combineDateTime(
            this.clockOutForm.date,
            this.clockOutForm.time,
          ),
          break_minutes: this.clockOutForm.break_minutes || 0,
          comment: this.clockOutForm.comment || undefined,
        })
        this.$bvModal.hide('clock-out-modal')
        this.$bvToast.toast('Clocked out successfully.', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
        this.fetchEvents()
      } catch (e) {
        this.$bvToast.toast('Clock out failed.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    async submitReject(bvModalEvent) {
      bvModalEvent.preventDefault()
      if (!this.rejectForm.eventId) return
      await this.approveEvent(
        this.rejectForm.eventId,
        'rejected',
        this.rejectForm.adminComment,
      )
      this.$bvModal.hide('reject-modal')
    },

    openRejectModal(event) {
      this.rejectForm.eventId = event.id
      this.rejectForm.adminComment = ''
      this.$bvModal.show('reject-modal')
    },
    startEditEvent(event) {
      const inParts = splitIso(event.clocked_in_at)
      const outParts = splitIso(event.clocked_out_at)
      this.editForm = {
        id: event.id,
        in_date: inParts.date,
        in_time: inParts.time,
        out_date: outParts.date,
        out_time: outParts.time,
        break_minutes: event.break_minutes || 0,
        comment: event.comment || '',
      }
      this.editingEventId = event.id
    },
    cancelEditEvent() {
      this.editingEventId = null
      this.resetEditForm()
    },
    resetEditForm() {
      this.editForm = {
        id: null,
        in_date: '',
        in_time: '',
        out_date: '',
        out_time: '',
        break_minutes: 0,
        comment: '',
      }
    },
    resetRejectForm() {
      this.rejectForm = { eventId: null, adminComment: '' }
    },
    async saveEditEvent() {
      try {
        const payload = { id: this.editForm.id }
        if (this.editForm.in_date)
          payload.clocked_in_at = combineDateTime(
            this.editForm.in_date,
            this.editForm.in_time,
          )
        if (this.editForm.out_date)
          payload.clocked_out_at = combineDateTime(
            this.editForm.out_date,
            this.editForm.out_time,
          )
        payload.break_minutes = this.editForm.break_minutes
        payload.comment = this.editForm.comment
        await this.$store.dispatch('timesheet/editEvent', payload)
        // Only leave edit mode on success — on failure the form (and the
        // user's in-progress edits) stays open so they can retry, matching
        // the old modal's behavior of staying open until it succeeded.
        this.editingEventId = null
        this.resetEditForm()
        this.$bvToast.toast('Timesheet updated.', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
        this.fetchEvents()
      } catch (e) {
        this.$bvToast.toast('Update failed.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    openEditLeaveModal(event) {
      this.editLeaveForm = {
        id: event.leave_id,
        worker_id: event.leave_worker_id,
        project_id: event.leave_project_id,
        leave_type: event.leave_type,
        start_date: event.leave_start_date,
        end_date: event.leave_end_date,
        include_weekends: !!event.leave_include_weekends,
        hours_per_day: event.leave_hours_per_day,
        note: event.leave_note || '',
      }
      this.$bvModal.show('edit-leave-modal')
    },
    resetEditLeaveForm() {
      this.editLeaveForm = emptyEditLeaveForm()
    },
    async submitEditLeave(bvModalEvent) {
      bvModalEvent.preventDefault()
      try {
        await this.$store.dispatch('timesheet/updateLeaveEntry', {
          id: this.editLeaveForm.id,
          worker_id: this.editLeaveForm.worker_id,
          project_id: this.editLeaveForm.project_id,
          leave_type: this.editLeaveForm.leave_type,
          start_date: this.editLeaveForm.start_date,
          end_date: this.editLeaveForm.end_date,
          // Same rule as create: a one-day entry always covers its own date,
          // whatever the toggle says, or saving it re-orphans a weekend
          // entry that was previously visible.
          include_weekends:
            this.editLeaveForm.start_date === this.editLeaveForm.end_date
              ? true
              : this.editLeaveForm.include_weekends,
          hours_per_day:
            this.editLeaveForm.hours_per_day === ''
              ? null
              : this.editLeaveForm.hours_per_day,
          note: this.editLeaveForm.note || undefined,
        })
        this.$bvModal.hide('edit-leave-modal')
        this.$bvToast.toast('Leave entry updated.', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
        this.fetchEvents()
      } catch (e) {
        this.$bvToast.toast(
          this.clockInErrorMessage(e, 'Failed to update leave entry.'),
          {
            title: 'Error',
            variant: 'danger',
            solid: true,
          },
        )
      }
    },

    async confirmDeleteLeave(event) {
      // Called both from the week-block's synthetic leave event (carries
      // leave_id + tradie) and from the edit modal's own footer (only has
      // editLeaveForm's id, no tradie name) — accept either shape.
      const leaveId = event.leave_id != null ? event.leave_id : event.id
      const ok = await this.$bvModal.msgBoxConfirm(
        'Delete this leave entry for ' +
          ((event.tradie && event.tradie.name) || 'this tradie') +
          '? This cannot be undone.',
        {
          title: 'Delete Entry',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          centered: true,
        },
      )
      if (!ok) return
      try {
        await this.$store.dispatch('timesheet/deleteLeaveEntry', leaveId)
        this.$bvModal.hide('edit-leave-modal')
        this.$bvToast.toast('Leave entry deleted.', {
          title: 'Deleted',
          variant: 'success',
          solid: true,
        })
        this.fetchEvents()
      } catch (e) {
        this.$bvToast.toast('Delete failed.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    async confirmDelete(event) {
      const ok = await this.$bvModal.msgBoxConfirm(
        'Delete this timesheet entry for ' +
          ((event.tradie && event.tradie.name) || 'this tradie') +
          '? This cannot be undone.',
        {
          title: 'Delete Entry',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          centered: true,
        },
      )
      if (!ok) return
      try {
        await this.$store.dispatch('timesheet/deleteEvent', event.id)
        this.$bvToast.toast('Entry deleted.', {
          title: 'Deleted',
          variant: 'success',
          solid: true,
        })
        this.fetchEvents()
      } catch (e) {
        this.$bvToast.toast('Delete failed.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    toastInvitedNeedsProject() {
      const h = this.$createElement
      const projectId = this.selectedProjectId
      const content = [h('div', 'Tradie invited to your company.')]
      if (projectId) {
        content.push(
          h(
            'b-link',
            {
              class: 'font-weight-bold',
              on: {
                click: () => {
                  this.$router.push({
                    name: 'projects.view-page',
                    params: { id: projectId },
                  })
                },
              },
            },
            'Add them to this project now',
          ),
        )
      } else {
        content.push(
          h('div', 'Add them to a project to include them on timesheets.'),
        )
      }
      this.$bvToast.toast(content, {
        title: 'Success',
        variant: 'success',
        solid: true,
        autoHideDelay: 8000,
      })
    },
    openInviteModal() {
      this.$bvModal.show('invite-tradie-modal')
    },
    resetInviteForm() {
      this.inviteForm = { email: '', loading: false }
    },
    async submitInvite(bvModalEvent) {
      bvModalEvent.preventDefault()
      this.inviteForm.loading = true
      try {
        await this.$store.dispatch('company/addTradie', this.inviteForm.email)
        this.$bvModal.hide('invite-tradie-modal')
        this.toastInvitedNeedsProject()
      } catch (e) {
        // company/addTradie rejects with err.response.data directly.
        const msg =
          (e && e.errors && e.errors.email && e.errors.email[0]) ||
          (e && e.message) ||
          'Could not send invite.'
        this.$bvToast.toast(msg, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.inviteForm.loading = false
      }
    },

    prefillExportDates() {
      this.exportOptions.dateFrom = this.dateFrom
      this.exportOptions.dateTo = this.dateTo
      this.exportOptions.exportProjectId = this.selectedProjectId
      this.exportTradieSearch = ''
    },

    async generateReport(bvModalEvent) {
      bvModalEvent.preventDefault()
      const opts = this.exportOptions
      const cols = opts.columns

      const diffDays = dayjs(opts.dateTo).diff(dayjs(opts.dateFrom), 'day')
      if (!opts.dateFrom || !opts.dateTo || diffDays < 0) {
        this.$bvToast.toast('Please select a valid date range.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
        return
      }
      let allEvents = []
      let leaveEntries = []
      try {
        let page = 1
        let lastPage = 1
        do {
          const params = {
            date_from: opts.dateFrom,
            date_to: opts.dateTo,
            per_page: 100,
            page,
          }
          if (opts.exportProjectId) params.project_id = opts.exportProjectId
          const res = await Api.get('/timesheet-events', { params })
          allEvents = allEvents.concat(res.data.events || [])
          lastPage =
            (res.data.meta && res.data.meta.last_page) ||
            res.data.last_page ||
            1
          page++
        } while (page <= lastPage)

        const leaveParams = {
          date_from: opts.dateFrom,
          date_to: opts.dateTo,
        }
        if (opts.exportProjectId) leaveParams.project_id = opts.exportProjectId
        const leaveRes = await Api.get('/leave-entries', {
          params: leaveParams,
        })
        leaveEntries = leaveRes.data.data || []
      } catch (e) {
        this.$bvToast.toast('Failed to fetch events for export.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
        return
      }

      let filtered = allEvents.filter((e) => opts.statuses.includes(e.status))
      if (opts.tradieIds.length) {
        filtered = filtered.filter(
          (e) => e.tradie && opts.tradieIds.includes(e.tradie.id),
        )
      }

      // Expand each leave entry into one synthetic row per day it covers,
      // clipped to the export range — mirrors leaveDayInstances but bounded
      // by the export's own date pickers instead of the on-screen week.
      let leaveRows = []
      leaveEntries.forEach((entry) => {
        if (!opts.leaveTypes.includes(entry.leave_type)) return
        let cursor = dayjs(entry.start_date).isAfter(dayjs(opts.dateFrom))
          ? dayjs(entry.start_date)
          : dayjs(opts.dateFrom)
        const end = dayjs(entry.end_date).isBefore(dayjs(opts.dateTo))
          ? dayjs(entry.end_date)
          : dayjs(opts.dateTo)
        let guard = 0
        while (
          cursor.format('YYYY-MM-DD') <= end.format('YYYY-MM-DD') &&
          guard < 400
        ) {
          const dayIso = cursor.format('YYYY-MM-DD')
          const isWeekend = cursor.day() === 0 || cursor.day() === 6
          if (entry.include_weekends || !isWeekend) {
            leaveRows.push(this.syntheticLeaveEvent(entry, dayIso))
          }
          cursor = cursor.add(1, 'day')
          guard += 1
        }
      })
      if (opts.tradieIds.length) {
        leaveRows = leaveRows.filter(
          (e) => e.tradie && opts.tradieIds.includes(e.tradie.id),
        )
      }

      filtered = filtered.concat(leaveRows)

      const statusSortKey = (e) =>
        e.is_leave ? leaveTypeLabel(e.leave_type) || '' : e.status || ''
      const dateSortKey = (e) => (e.is_leave ? e.leave_date : e.clocked_in_at)

      const sortFns = {
        date: (a, b) => new Date(dateSortKey(b)) - new Date(dateSortKey(a)),
        date_asc: (a, b) => new Date(dateSortKey(a)) - new Date(dateSortKey(b)),
        tradie: (a, b) =>
          ((a.tradie && a.tradie.name) || '').localeCompare(
            (b.tradie && b.tradie.name) || '',
          ),
        project: (a, b) =>
          ((a.project && a.project.name) || '').localeCompare(
            (b.project && b.project.name) || '',
          ),
        status: (a, b) => statusSortKey(a).localeCompare(statusSortKey(b)),
      }
      filtered = filtered.slice().sort(sortFns[opts.sortBy] || sortFns.date)

      const rows = buildTimesheetExportRows(filtered, cols)

      const filename = 'Timesheet Report ' + dayjs().format('DD MM YYYY')

      if (opts.format === 'pdf') {
        downloadRowsAsPdf(filename, rows)
      } else {
        downloadRowsAsCsv(filename, rows)
      }
      this.$bvModal.hide('export-report-modal')
    },

    openLightbox(urls, index) {
      this.lightboxPhotos = urls.slice()
      this.lightboxIndex = index
      this.$bvModal.show('photo-lightbox-modal')
    },
    resetLightbox() {
      this.lightboxPhotos = []
      this.lightboxIndex = 0
    },
    lightboxPrev() {
      this.lightboxIndex =
        (this.lightboxIndex - 1 + this.lightboxPhotos.length) %
        this.lightboxPhotos.length
    },
    lightboxNext() {
      this.lightboxIndex = (this.lightboxIndex + 1) % this.lightboxPhotos.length
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/layouts/timesheet-shared';

.timesheet-list-tab {
  min-height: 700px;
  background: #f8fafc;
  border-radius: 0 0 8px 8px;
}

.btn {
  font-weight: 600;
  border-radius: 8px;
}

.list-content {
  padding: 18px 20px 22px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 24px;
  color: #9ca3af;

  p {
    margin: 12px 0 4px;
    font-size: 14px;
  }
}

.empty-icon {
  font-size: 2.5rem;
  color: #d1d5db;
}

.tradie-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* .tradie-card / .tradie-row / .tradie-row-left / .tradie-name /
   .tradie-total-hours / .tradie-row-summary / .summary-pill / .no-entries /
   .action-btn / .action-menu / .kebab / .action-menu-dropdown / .tradie-detail
   moved to TradieRow.vue's own scoped style - this file's copies never
   applied once that markup was split into its own component (Vue scoped CSS
   doesn't reach a child's internal nodes, only its root element). */

.bottom-actions {
  display: flex;
  gap: 8px;
  padding: 4px 0;
}
</style>
